import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { formatDateSlashes } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  pair: [SalesforceSurchargePeriod, SalesforceSurchargePeriod] | null;
  currentIndex: number;
  totalPairs: number;
}

export function NonAccumulatingConflictDialog({ open, onCancel, onConfirm, pair, currentIndex, totalPairs }: Props) {
  if (!pair) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="non-accu-conflict-title" maxWidth="sm" fullWidth>
      <DialogTitle id="non-accu-conflict-title">
        Confirm overlapping surcharge configuration
        {totalPairs > 1 && (
          <Typography variant="body2" color="text.secondary">
            Conflict {currentIndex + 1} of {totalPairs}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText component="div">
            These two surcharges overlap in date and weekday but have different accumulation rules. Please confirm this
            matches what the PCM intended and is <strong>not a Salesforce load error</strong>.
          </DialogContentText>
          <List dense disablePadding>
            {pair.map((s) => (
              <ListItem key={s.id} disableGutters>
                <ListItemText
                  primary={s.name}
                  secondary={`${formatDateSlashes(s.start_date)} → ${formatDateSlashes(s.end_date)} · Accumulate: ${
                    s.accumulate ? 'Yes' : 'No'
                  } · Overlapping: ${s.overlapping ? 'Yes' : 'No'}`}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm}>
          {currentIndex < totalPairs - 1 ? 'Confirm and next' : 'Confirm and proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
